// client/src/hooks/useCamps.js
// TanStack Query hooks for the camps resource.

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api.js';

const CAMPS_KEY = ['camps'];
const campKey = (id) => ['camps', id];

export function useCamps() {
  return useQuery({
    queryKey: CAMPS_KEY,
    queryFn: () => api.get('/api/camps').then((r) => r.camps),
  });
}

export function useCamp(campId) {
  return useQuery({
    queryKey: campKey(campId),
    queryFn: () => api.get(`/api/camps/${campId}`).then((r) => r.camp),
    enabled: !!campId,
  });
}

export function useCreateCamp() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) => api.post('/api/camps', body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: CAMPS_KEY });
      // Also refresh /api/me so a manager's managedCamps list updates
      // immediately after a super-admin invites them (relevant later when
      // sub-task 4 chains invite-on-create).
      qc.invalidateQueries({ queryKey: ['me'] });
    },
  });
}

export function useUpdateCamp(campId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) => api.patch(`/api/camps/${campId}`, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: CAMPS_KEY });
      qc.invalidateQueries({ queryKey: campKey(campId) });
    },
  });
}

export function useDeleteCamp() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (campId) => api.delete(`/api/camps/${campId}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: CAMPS_KEY });
      qc.invalidateQueries({ queryKey: ['me'] });
    },
  });
}

/**
 * Multipart logo upload (PUT, replaces any existing logo). Raw fetch because
 * we need multipart/form-data, not JSON — same cookie-credentialed shape as
 * the XLSX import. Server normalizes the image to a fixed square PNG.
 */
async function uploadCampLogo(campId, file) {
  const fd = new FormData();
  fd.append('logo', file);
  const res = await fetch(`/api/camps/${campId}/logo`, {
    method: 'PUT',
    body: fd,
    credentials: 'same-origin',
  });
  const text = await res.text();
  let payload = null;
  try { payload = text ? JSON.parse(text) : null; } catch { /* leave null */ }
  if (!res.ok) {
    const err = new Error(payload?.message || payload?.error || `HTTP ${res.status}`);
    err.status = res.status;
    err.code = payload?.error;
    throw err;
  }
  return payload;
}

export function useUploadCampLogo(campId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (file) => uploadCampLogo(campId, file),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: campKey(campId) });
      qc.invalidateQueries({ queryKey: CAMPS_KEY });
    },
  });
}

/**
 * "מעבר לשנה חדשה" — analyze (dryRun) + commit against the promote endpoint.
 * Not a useMutation pair because the wizard drives both calls imperatively
 * and needs the raw report either way (same pattern as usePointsImport).
 */
export function usePromoteCamp(campId) {
  const qc = useQueryClient();
  const base = `/api/camps/${campId}/promote`;
  return {
    analyze: (body) => api.post(`${base}?dryRun=1`, body),
    commit: async (body) => {
      const report = await api.post(base, body);
      if (!report.blocked) {
        // Source loses its cards, target gains students + classes.
        for (const id of [campId, body.targetCampId]) {
          qc.invalidateQueries({ queryKey: ['camps', id, 'students'] });
          qc.invalidateQueries({ queryKey: ['camps', id, 'classes'] });
        }
        qc.invalidateQueries({ queryKey: CAMPS_KEY });
      }
      return report;
    },
  };
}

export function useDeleteCampLogo(campId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.delete(`/api/camps/${campId}/logo`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: campKey(campId) });
      qc.invalidateQueries({ queryKey: CAMPS_KEY });
    },
  });
}
